import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

// Open/close state for the StackIQ side panel. Lives above the router so the
// panel (and its in-flight conversation) survives page navigation.
type AiPanelContextValue = {
  isOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
};

const AiPanelContext = createContext<AiPanelContextValue>({
  isOpen: false,
  openPanel: () => {},
  closePanel: () => {},
  togglePanel: () => {},
});

export const AiPanelProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const openPanel = useCallback(() => setIsOpen(true), []);
  const closePanel = useCallback(() => setIsOpen(false), []);
  const togglePanel = useCallback(() => setIsOpen((open) => !open), []);
  const value = useMemo(
    () => ({ isOpen, openPanel, closePanel, togglePanel }),
    [isOpen, openPanel, closePanel, togglePanel]
  );
  return <AiPanelContext.Provider value={value}>{children}</AiPanelContext.Provider>;
};

export const useAiPanel = () => useContext(AiPanelContext);

export default AiPanelContext;
